import { css } from '@emotion/react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Chip, IconButton, Stack, Tooltip } from '@mui/material';
import {
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
  KeyCreatorParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { FieldCellEditor } from '../../../components/AgTable/cell-editors/FieldCellEditor';
import {
  CompanyMappingStatus,
  CompanyMappingStatusUI,
  CompanyMappingVM,
  companyMappingVMSchema,
  MappingStatusToLabel,
  willCreateMapping,
} from '../../../schemas/CompanyMappings.schema';
import { editableCellClass, nonEditableCellClass } from '../../../theme/gridStyle';
import { withOverrides } from '../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { MenuButton } from '../../Finance2/common-grid-defs/MenuButton';
import { LinkRenderer } from '../../PortfolioOverview/components/OverviewTable/columns/TypeBasedConfigs/TypeCellRenderers/LinkRenderer';
import { useMappingsCompanyField } from './MappingsCompanyField';
import { useUpdateCompanyMappings } from './useUpdateCompanyMappings';

const nonActionableStatuses = new Set<string>([
  CompanyMappingStatusUI.mapped,
  CompanyMappingStatusUI.skipped,
]);

const INVESTRAN_PROVIDER = 'Investran';

// Not every client runs Investran as their fund accounting system, so the provider reads generically.
const providerToLabel = new Map<string, string>([[INVESTRAN_PROVIDER, 'General Ledger']]);

export function providerFormatter(
  params: KeyCreatorParams<CompanyMappingVM> | ValueFormatterParams<CompanyMappingVM>
) {
  if (!params.value) {
    return '';
  }
  return providerToLabel.get(params.value as string) ?? String(params.value);
}

export function useCompanyMappingsColDefs() {
  const { formFieldInvestran, formFieldPortfolio, doOnChange } = useMappingsCompanyField();
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = companyMappingVMSchema().pick(['provider', 'providerCompanyName', 'company', 'status']);
    const colDefs = schemaToGrid(schema);
    const overrides: Record<string, ColDef> = {
      provider: {
        ...defaultColDefs,
        cellClass: nonEditableCellClass,
        enableRowGroup: true,
        maxWidth: 130,
        valueFormatter: providerFormatter,
        keyCreator: providerFormatter,
        filterParams: {
          ...defaultColDefs.filterParams,
          valueFormatter: providerFormatter,
        },
      },
      providerCompanyName: {
        initialSort: 'asc',
      },
      company: {
        ...defaultColDefs,
        field: 'company',
        headerName: 'Foresight Company Name',
        editable: true,
        cellEditor: FieldCellEditor,
        cellEditorParams: (params: ICellEditorParams<CompanyMappingVM>) => ({
          formField: params.data?.provider === INVESTRAN_PROVIDER ? formFieldInvestran : formFieldPortfolio,
          doOnChange,
        }),
        valueFormatter: (params: ValueFormatterParams<CompanyMappingVM>) => {
          return params.data?.company?.fields?.name ?? '';
        },
        cellRenderer: CompanyCellRenderer,
        cellClass: editableCellClass,
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          // without this, selecting an option with 'Enter' triggers stopEditing before the value is set
          return params.event.key === 'Enter' && params.editing;
        },
        filterParams: {
          valueFormatter: (params: ValueFormatterParams<CompanyMappingVM>) => {
            return params.value?.fields?.name ?? '';
          },
        },
      },
      status: {
        enableRowGroup: true,
        valueGetter: (params: ValueGetterParams<CompanyMappingVM>) => params.data?.status,
        valueFormatter: statusValueFormatter,
        filterParams: {
          valueFormatter: statusValueFormatter,
        },
        comparator: (a, b) => {
          return (
            mappingStatusToSortOrder[a as CompanyMappingStatusUI] -
            mappingStatusToSortOrder[b as CompanyMappingStatusUI]
          );
        },
      },
    };

    const actionColDef: ColDef = {
      cellRenderer: MappingActionRenderer,
      filter: false,
      width: 100,
      maxWidth: 100,
      minWidth: 100,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
    };

    colDefs.splice(colDefs.length - 1, 0, {
      field: 'company.fields.website',
      headerName: 'Website',
      cellClass: nonEditableCellClass,
      cellRenderer: LinkRenderer,
    });

    return [...withOverrides(colDefs, overrides), actionColDef];
  }, [doOnChange, formFieldInvestran, formFieldPortfolio, schemaToGrid]);
}

export const defaultColDefs = {
  flex: 1,
  filter: true,
  sortable: true,
  minWidth: 150,
  filterParams: {
    buttons: ['reset'],
  },
};

function statusValueFormatter(params: ValueFormatterParams<CompanyMappingVM>) {
  return params.value ? (MappingStatusToLabel[params.value as CompanyMappingStatus] ?? params.value) : '';
}

const companyNameCss = css({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
const willCreateChipCss = css({ flexShrink: 0 });

export function CompanyCellRenderer(params: ICellRendererParams<CompanyMappingVM>) {
  const name = params.valueFormatted;
  if (!name) {
    return null;
  }

  const willCreate = willCreateMapping(params.data);

  return (
    <Stack direction='row' alignItems='center' gap='0.5rem' height='100%' overflow='hidden'>
      <span css={companyNameCss}>{name}</span>
      {willCreate && (
        <Chip label='Will create' size='small' color='info' variant='outlined' css={willCreateChipCss} />
      )}
    </Stack>
  );
}

export function MappingActionRenderer(params: ICellRendererParams<CompanyMappingVM>) {
  const { data } = params;
  const { createMappings, skipMapping, isUpdating } = useUpdateCompanyMappings();
  const canSave = Boolean(data?.company?.fields?.name);

  const menuItems = useMemo(() => {
    if (!data) {
      return [];
    }
    const res = [
      {
        icon: <VisibilityOffOutlinedIcon />,
        name: 'Skip',
        onClick: () => {
          skipMapping(data);
        },
      },
    ];

    if (data?.company?.fields?.name) {
      res.push({
        icon: <CheckOutlinedIcon />,
        name: 'Save',
        onClick: () => {
          createMappings([data]);
        },
      });
    }
    return res;
  }, [createMappings, data, skipMapping]);

  if (!data) {
    return null;
  }
  if (!data.modified && nonActionableStatuses.has(data.status)) {
    return null;
  }

  return (
    <Stack direction='row' height='100%' width='100%' alignItems='center' justifyContent='flex-end'>
      {canSave && (
        <Tooltip title='Save'>
          <span>
            <IconButton
              size='small'
              aria-label='Save'
              disabled={isUpdating}
              onClick={() => createMappings([data])}
            >
              <CheckOutlinedIcon fontSize='small' color={isUpdating ? 'disabled' : 'secondary'} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <MenuButton menuItems={menuItems} />
    </Stack>
  );
}

const mappingStatusToSortOrder: Record<CompanyMappingStatusUI, number> = {
  [CompanyMappingStatusUI.modified]: 0,
  [CompanyMappingStatusUI.init]: 1,
  [CompanyMappingStatusUI.preMapped]: 2,
  [CompanyMappingStatusUI.skipped]: 4,
  [CompanyMappingStatusUI.mapped]: 3,
};
